import React, { useState, useEffect } from "react";
import { FaTimes, FaPlus, FaEdit, FaTrash, FaSave, FaCog } from "react-icons/fa";
import { monitoringApi } from "../../api/monitoring.api";
import "./alert-rules-modal.css";

const RULE_TYPES = [
    { value: 'FAILURE_RATE', label: 'Tasa de fallos (%)', unit: '%' },
    { value: 'NO_ACTIVITY', label: 'Sin actividad (minutos)', unit: 'min' },
    { value: 'LOAD_SPIKE', label: 'Pico de carga (eventos/min)', unit: 'ev/min' },
    { value: 'CONSECUTIVE_FAILURES', label: 'Fallos consecutivos (cantidad)', unit: 'fallos' },
];

const SEVERITIES = [
    { value: 'INFO', color: '#3b82f6', label: 'Informativo' },
    { value: 'WARNING', color: '#f59e0b', label: 'Advertencia' },
    { value: 'CRITICAL', color: '#ef4444', label: 'Crítico' },
];

const DEFAULT_FORM = {
    name: '',
    description: '',
    ruleType: 'FAILURE_RATE',
    targetModule: '',
    thresholdValue: 20,
    windowMinutes: 5,
    severity: 'WARNING',
    enabled: true,
};

function AlertRulesModal({ onClose, onSave }) {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | rule.id
    const [form, setForm] = useState(DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    useEffect(() => {
        loadRules();
    }, []);

    const loadRules = async () => {
        try {
            const data = await monitoringApi.getAlertRules();
            setRules(data);
        } catch (e) {
            console.error("Error loading rules:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (rule) => {
        setEditing(rule.id);
        setForm({
            name: rule.name,
            description: rule.description || '',
            ruleType: rule.ruleType,
            targetModule: rule.targetModule || '',
            thresholdValue: rule.thresholdValue,
            windowMinutes: rule.windowMinutes,
            severity: rule.severity,
            enabled: rule.enabled,
        });
    };

    const handleNew = () => {
        setEditing('new');
        setForm(DEFAULT_FORM);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const payload = {
                ...form,
                targetModule: form.targetModule.trim() || null,
            };
            if (editing === 'new') {
                await monitoringApi.createAlertRule(payload);
            } else {
                await monitoringApi.updateAlertRule(editing, payload);
            }
            await loadRules();
            setEditing(null);
            onSave && onSave();
        } catch (e) {
            console.error("Error saving rule:", e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        setDeleting(id);
        try {
            await monitoringApi.deleteAlertRule(id);
            await loadRules();
            onSave && onSave();
        } catch (e) {
            console.error("Error deleting rule:", e);
        } finally {
            setDeleting(null);
        }
    };

    const handleToggle = async (rule) => {
        try {
            await monitoringApi.updateAlertRule(rule.id, { ...rule, enabled: !rule.enabled });
            await loadRules();
        } catch (e) {
            console.error("Error toggling rule:", e);
        }
    };

    const ruleTypeMeta = (type) => RULE_TYPES.find(r => r.value === type) || RULE_TYPES[0];
    const severityMeta = (s) => SEVERITIES.find(sv => sv.value === s) || SEVERITIES[1];

    return (
        <div className="arm-overlay" onClick={onClose}>
            <div className="arm-modal" onClick={e => e.stopPropagation()}>
                <div className="arm-header">
                    <h2><FaCog /> Reglas de Alerta</h2>
                    <button className="arm-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="arm-body">
                    {/* Edit form. */}
                    {editing && (
                        <div className="arm-form">
                            <h3>{editing === 'new' ? 'Nueva regla' : 'Editar regla'}</h3>
                            <div className="arm-form-grid">
                                <div className="arm-field">
                                    <label>Nombre *</label>
                                    <input
                                        value={form.name}
                                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                        placeholder="Nombre descriptivo"
                                    />
                                </div>
                                <div className="arm-field">
                                    <label>Tipo de regla</label>
                                    <select
                                        value={form.ruleType}
                                        onChange={e => setForm(p => ({ ...p, ruleType: e.target.value }))}
                                    >
                                        {RULE_TYPES.map(rt => (
                                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="arm-field">
                                    <label>Umbral ({ruleTypeMeta(form.ruleType).unit})</label>
                                    <input
                                        type="number"
                                        value={form.thresholdValue}
                                        onChange={e => setForm(p => ({ ...p, thresholdValue: e.target.value }))}
                                        min="0"
                                    />
                                </div>
                                <div className="arm-field">
                                    <label>Ventana (minutos)</label>
                                    <input
                                        type="number"
                                        value={form.windowMinutes}
                                        onChange={e => setForm(p => ({ ...p, windowMinutes: e.target.value }))}
                                        min="1"
                                    />
                                </div>
                                <div className="arm-field">
                                    <label>Módulo objetivo (vacío = todos)</label>
                                    <input
                                        value={form.targetModule}
                                        onChange={e => setForm(p => ({ ...p, targetModule: e.target.value }))}
                                        placeholder="Ej: ms-build, ms-emisiones..."
                                    />
                                </div>
                                <div className="arm-field">
                                    <label>Severidad</label>
                                    <select
                                        value={form.severity}
                                        onChange={e => setForm(p => ({ ...p, severity: e.target.value }))}
                                    >
                                        {SEVERITIES.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="arm-field arm-field-full">
                                    <label>Descripción</label>
                                    <textarea
                                        value={form.description}
                                        onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                        placeholder="Descripción de la regla..."
                                        rows={2}
                                    />
                                </div>
                            </div>
                            <div className="arm-form-actions">
                                <button className="arm-btn-cancel" onClick={() => setEditing(null)}>
                                    <FaTimes /> Cancelar
                                </button>
                                <button className="arm-btn-save" onClick={handleSave} disabled={saving || !form.name.trim()}>
                                    <FaSave /> {saving ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Rule list. */}
                    <div className="arm-rules-header">
                        <span className="arm-rules-count">{rules.length} reglas configuradas</span>
                        <button className="arm-btn-new" onClick={handleNew}>
                            <FaPlus /> Nueva regla
                        </button>
                    </div>

                    {loading ? (
                        <p className="arm-loading">Cargando reglas...</p>
                    ) : rules.length === 0 ? (
                        <p className="arm-empty">No hay reglas configuradas.</p>
                    ) : (
                        <div className="arm-rules-list">
                            {rules.map(rule => {
                                const sv = severityMeta(rule.severity);
                                return (
                                    <div key={rule.id} className={`arm-rule ${!rule.enabled ? 'disabled' : ''}`}>
                                        <div className="arm-rule-left">
                                            <div
                                                className="arm-rule-severity"
                                                style={{ background: sv.color + '22', color: sv.color }}
                                            >
                                                {sv.label}
                                            </div>
                                            <div className="arm-rule-info">
                                                <span className="arm-rule-name">{rule.name}</span>
                                                <span className="arm-rule-meta">
                                                    {ruleTypeMeta(rule.ruleType).label} · Umbral: {rule.thresholdValue} · Ventana: {rule.windowMinutes}min
                                                    {rule.targetModule && ` · Módulo: ${rule.targetModule}`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="arm-rule-actions">
                                            <button
                                                className={`arm-toggle ${rule.enabled ? 'on' : 'off'}`}
                                                onClick={() => handleToggle(rule)}
                                                title={rule.enabled ? 'Deshabilitar' : 'Habilitar'}
                                            >
                                                {rule.enabled ? 'Activa' : 'Inactiva'}
                                            </button>
                                            <button
                                                className="arm-action-btn edit"
                                                onClick={() => handleEdit(rule)}
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="arm-action-btn delete"
                                                onClick={() => handleDelete(rule.id)}
                                                disabled={deleting === rule.id}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AlertRulesModal;
